import { useEffect } from 'react';
import { useTracking } from 'react-tracking';

import loadable from '@loadable/component';
import { useNarrative } from '@/pages/Narrative/hooks/useNarrative';
import { Footer } from '@components/Footer';
import { Sentry } from '@/components/Sentry';
import useUser from '@/hooks/useUser';

import { useNarrativeSlug } from '../../hooks';
import PageSkeleton from './components/PageSkeleton';
import { NarrativeSchema } from './components/NarrativeSchema';
import { setLastVisitedStockForMiniValuator } from '../../utils/storage';

import If from '@/components/If';
import { useFlag } from '@simplywallst/unleash';
import { NarrativeNotFound } from '../NarrativeNotFound';
import { narrativeRetry } from '../../utils/error';
import { NarrativeLayout } from './components/NarrativeLayout';
import { NarrativeInvalidationBoundary } from '../NarrativeInvalidationBoundary';
import { handleOnSetPrimary } from './utils';

const NarrativeGuestRegistrationModal = loadable(
  () => import('@/components/NarrativeGuestRegistrationModal'),
  {
    resolveComponent: (component) => component.NarrativeGuestRegistrationModal,
  }
);

export const Narrative = () => {
  const { slug } = useNarrativeSlug();
  const {
    data: narrativeResponse,
    isLoading,
    error,
  } = useNarrative(
    { slug },
    {
      retry: narrativeRetry,
    }
  );
  const narrative = narrativeResponse?.narrative;
  const { data: user } = useUser();
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'narrative',
    companyId: narrative?.companyId,
    uniqueSymbol: narrative?.company?.uniqueSymbol,
    narrativeId: narrative?.id,
    userId: user?.id,
    owner: {
      publicId: narrative?.owner.publicId,
      displayName: narrative?.owner.displayName,
      role: narrative?.owner.role,
    },
  });

  useEffect(() => {
    if (narrative?.id) {
      if (slug && !window.location.hash) {
        window.scrollTo(0, 0);
      }
      trackEvent({
        action: 'view',
        announcementCount: narrative.announcements?.length,
      });
      if (narrative.company?.canonicalURL) {
        setLastVisitedStockForMiniValuator(narrative?.company?.canonicalURL);
      }
    }
  }, [narrative, slug, trackEvent]);

  const canShowRegistration = !user.loggedIn;

  const { ready, enabled } = useFlag('mono-narrative-registration-modal');

  if (isLoading) return <PageSkeleton />;

  if (error || !narrative) return <NarrativeNotFound />;

  return (
    <Track>
      <div className="relative mt-x0 w-full max-w-screen-xl md:mt-x3">
        <Sentry
          sentryCapture={{ team: 'narwhals', extra: { slug } }}
          globalDisabled
        >
          <NarrativeSchema narrative={narrative} />
          <NarrativeInvalidationBoundary
            onSetPrimary={handleOnSetPrimary(
              useNarrative.getOptions({ slug }).queryKey
            )}
          >
            <NarrativeLayout narrative={narrative} />
          </NarrativeInvalidationBoundary>
          <Footer />
          <If match={ready && enabled && canShowRegistration}>
            <NarrativeGuestRegistrationModal />
          </If>
        </Sentry>
      </div>
    </Track>
  );
};
